// frontend/modules/document-type/hooks/useDocumentTypeValidation.ts
import { useState } from 'react';
import { DocumentTypeFormData, FileValidationResult } from '../types/document-type.types';

interface ValidationErrors {
    code?: string;
    name?: string;
    minUploadSizeKb?: string;
    maxUploadSizeKb?: string;
    allowedFileTypes?: string;
    displayOrder?: string;
}

export const useDocumentTypeValidation = () => {
    const [errors, setErrors] = useState<ValidationErrors>({});

    const validateDocumentType = (data: Partial<DocumentTypeFormData>): boolean => {
        const newErrors: ValidationErrors = {};

        // Validate code
        if (!data.code || data.code.trim() === '') {
            newErrors.code = 'Document code is required';
        } else if (data.code.length > 50) {
            newErrors.code = 'Document code must not exceed 50 characters';
        } else if (!/^[A-Za-z0-9_-]+$/.test(data.code)) {
            newErrors.code = 'Document code can only contain letters, numbers, hyphens, and underscores';
        }

        // Validate name
        if (!data.name || data.name.trim() === '') {
            newErrors.name = 'Document name is required';
        } else if (data.name.length > 100) {
            newErrors.name = 'Document name must not exceed 100 characters';
        }

        // Validate size constraints
        if (data.minUploadSizeKb !== undefined && data.minUploadSizeKb !== null) {
            if (data.minUploadSizeKb < 0) {
                newErrors.minUploadSizeKb = 'Minimum upload size must be non-negative';
            }
        }

        if (data.maxUploadSizeKb !== undefined && data.maxUploadSizeKb !== null) {
            if (data.maxUploadSizeKb < 1) {
                newErrors.maxUploadSizeKb = 'Maximum upload size must be at least 1 KB';
            }
        }

        // Validate size relationship
        if (
            data.minUploadSizeKb !== undefined && data.minUploadSizeKb !== null &&
            data.maxUploadSizeKb !== undefined && data.maxUploadSizeKb !== null &&
            data.minUploadSizeKb > data.maxUploadSizeKb
        ) {
            newErrors.maxUploadSizeKb = 'Maximum upload size must be greater than minimum upload size';
        }

        // Validate display order
        if (data.displayOrder !== undefined && data.displayOrder !== null && data.displayOrder < 0) {
            newErrors.displayOrder = 'Display order must be non-negative';
        }

        // Validate allowed file types
        if (data.allowedFileTypes && data.allowedFileTypes.length > 0) {
            const invalidTypes = data.allowedFileTypes.filter(type => 
                !type || type.trim() === '' || type.length > 10
            );
            if (invalidTypes.length > 0) {
                newErrors.allowedFileTypes = 'All file types must be valid and not exceed 10 characters';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateFile = (
        fileName: string,
        fileSizeBytes: number,
        documentType: DocumentTypeFormData
    ): FileValidationResult => {
        const errors: string[] = [];
        const fileSizeKb = fileSizeBytes / 1024;

        // Check file size constraints
        if (documentType.minUploadSizeKb && fileSizeKb < documentType.minUploadSizeKb) {
            errors.push(`File size (${fileSizeKb.toFixed(2)} KB) is below minimum required size (${documentType.minUploadSizeKb} KB)`);
        }

        if (documentType.maxUploadSizeKb && fileSizeKb > documentType.maxUploadSizeKb) {
            errors.push(`File size (${fileSizeKb.toFixed(2)} KB) exceeds maximum allowed size (${documentType.maxUploadSizeKb} KB)`);
        }

        // Check file type constraints
        if (documentType.allowedFileTypes && documentType.allowedFileTypes.length > 0) {
            const fileExtension = getFileExtension(fileName);
            if (!fileExtension) {
                errors.push('File must have a valid extension');
            } else {
                const isAllowed = documentType.allowedFileTypes.some(
                    allowedType => allowedType.toLowerCase() === fileExtension.toLowerCase()
                );
                if (!isAllowed) {
                    errors.push(`File type '${fileExtension}' is not allowed. Allowed types: ${documentType.allowedFileTypes.join(', ')}`);
                }
            }
        }

        return {
            valid: errors.length === 0,
            fileName,
            fileSizeBytes,
            documentTypeCode: documentType.code,
            errors: errors.length > 0 ? errors : undefined
        };
    };

    const getFileExtension = (fileName: string): string | null => {
        if (!fileName || fileName.trim() === '') {
            return null;
        }

        const lastDotIndex = fileName.lastIndexOf('.');
        if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
            return null;
        }

        return fileName.substring(lastDotIndex + 1);
    };

    const clearErrors = () => {
        setErrors({});
    };

    const getFieldError = (field: keyof ValidationErrors): string | undefined => {
        return errors[field];
    };

    const hasErrors = (): boolean => {
        return Object.keys(errors).length > 0;
    };

    return {
        errors,
        validateDocumentType,
        validateFile,
        getFileExtension,
        clearErrors,
        getFieldError,
        hasErrors
    };
};
